import React, { useState, useEffect } from "react";
import BaseTailoringQuestions from "../shared/BaseTailoringQuestions";
import type { TailoringQuestion } from "../shared/BaseTailoringQuestions";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { AlertCircle, FileText, FileSignature } from "lucide-react";
import auditReportData from "../../constants/ConcludingProcedures/Audit_Report.json";
import PlanningSignoffDialog, { PlanningSignoffTokens } from "../planning/PlanningSignoffDialog";
import { useAuth } from "@/contexts/AuthContext";
import { loadMiscSignoffs, getSignoffEntriesForCard, type MiscSignoffEntry } from "@/utils/misc-signoff-utils";
import { useEngagement } from "@/contexts/EngagementContext";
import { apiService } from "@/services/api-service";

interface AuditReportAndOpinionLandingProps {
  onSelect?: (section: string) => void;
}

// Transform JSON structure to TailoringQuestion format
function transformJSONToQuestions(jsonData: any): TailoringQuestion[] {
  const questions: TailoringQuestion[] = [];
  const auditData = jsonData.auditReportAndOpinion;

  // Helper to create a question
  const createQuestion = (
    id: string,
    questionText: string,
    type: "radio" | "text",
    options?: string[],
    followUp?: TailoringQuestion["follow_up"]
  ): TailoringQuestion => {
    return {
      id,
      question: questionText,
      type,
      options,
      follow_up: followUp,
    };
  };

  // 1. Financial Framework
  if (auditData.financialFramework) {
    const q = auditData.financialFramework;
    questions.push(
      createQuestion(
        "financialFramework",
        q.question,
        "radio",
        q.options?.map((opt: any) => opt.label) || []
      )
    );
  }

  // 2. Key Audit Matters
  if (auditData.keyAuditMatters) {
    const q = auditData.keyAuditMatters;
    const followUp: TailoringQuestion["follow_up"] = q.ifYes
      ? {
          id: "keyAuditMatters_documentation",
          question: q.ifYes.documentationLabel || "Document your considerations:",
          type: "text",
          condition: "Yes",
        }
      : undefined;
    questions.push(
      createQuestion("keyAuditMatters", q.question, "radio", ["Yes", "No"], followUp)
    );
  }

  // 3. Emphasis of Matter
  if (auditData.emphasisOfMatter) {
    const q = auditData.emphasisOfMatter;
    const followUp: TailoringQuestion["follow_up"] = q.ifYes
      ? {
          id: "emphasisOfMatter_documentation",
          question: q.ifYes.documentationLabel || "Document your considerations:",
          type: "text",
          condition: "Yes",
        }
      : undefined;
    questions.push(
      createQuestion("emphasisOfMatter", q.question, "radio", ["Yes", "No"], followUp)
    );
  }

  // 4. Other Matter
  if (auditData.otherMatter) {
    const q = auditData.otherMatter;
    const followUp: TailoringQuestion["follow_up"] = q.ifYes
      ? {
          id: "otherMatter_documentation",
          question: q.ifYes.documentationLabel || "Document your considerations:",
          type: "text",
          condition: "Yes",
        }
      : undefined;
    questions.push(
      createQuestion("otherMatter", q.question, "radio", ["Yes", "No"], followUp)
    );
  }

  // 5. Going Concern
  if (auditData.goingConcern) {
    const q = auditData.goingConcern;
    const followUp: TailoringQuestion["follow_up"] = q.ifYes
      ? {
          id: "goingConcern_documentation",
          question: q.ifYes.documentationLabel || "Document your assessment:",
          type: "text",
          condition: "Yes",
        }
      : undefined;
    questions.push(
      createQuestion("goingConcern", q.question, "radio", ["Yes", "No"], followUp)
    );
  }

  // 6. Corresponding Information
  if (auditData.correspondingInformation) {
    const q = auditData.correspondingInformation;
    const followUp: TailoringQuestion["follow_up"] = q.ifYes
      ? {
          id: "correspondingInformation_documentation",
          question: q.ifYes.documentationLabel || "Document if Yes",
          type: "text",
          condition: "Yes",
        }
      : undefined;
    questions.push(
      createQuestion("correspondingInformation", q.question, "radio", ["Yes", "No"], followUp)
    );
  }

  // 7. Comparative Financial Statements
  if (auditData.comparativeFinancialStatements) {
    const q = auditData.comparativeFinancialStatements;
    const followUp: TailoringQuestion["follow_up"] = q.ifYes
      ? {
          id: "comparativeFinancialStatements_documentation",
          question: q.ifYes.documentationLabel || "Document if Yes",
          type: "text",
          condition: "Yes",
        }
      : undefined;
    questions.push(
      createQuestion("comparativeFinancialStatements", q.question, "radio", ["Yes", "No"], followUp)
    );
  }

  // 8. Audit Opinion
  if (auditData.auditOpinion) {
    const q = auditData.auditOpinion;
    // Use labels directly from the Word document (they already include all the text)
    const options = q.options?.map((opt: any) => opt.label) || [];
    questions.push(
      createQuestion("auditOpinion", q.question, "radio", options)
    );
  }

  return questions;
}

export default function AuditReportAndOpinionLanding(_props: AuditReportAndOpinionLandingProps) {
  const [questions, setQuestions] = useState<TailoringQuestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuth();
  const { currentEngagement } = useEngagement();
  const [userEngagementRole, setUserEngagementRole] = useState<string | null>(null);
  const [signoffDialogOpen, setSignoffDialogOpen] = useState(false);
  const [cardSignoffs, setCardSignoffs] = useState<MiscSignoffEntry[]>([]);

  const loadCardSignoffs = React.useCallback(async () => {
    try {
      const data = await loadMiscSignoffs();
      const name = "Audit Report & Opinion";
      setCardSignoffs(getSignoffEntriesForCard(name, name, data.signoff));
    } catch {
      setCardSignoffs([]);
    }
  }, []);

  useEffect(() => {
    const loadQuestions = async () => {
      try {
        setLoading(true);
        setError(null);

        // Use the imported JSON data directly
        const jsonData = auditReportData;

        const transformedQuestions = transformJSONToQuestions(jsonData);
        setQuestions(transformedQuestions);
      } catch (err) {
        console.error("Failed to load questions:", err);
        setError(err instanceof Error ? err.message : "Failed to load questions");
      } finally {
        setLoading(false);
      }
    };

    loadQuestions();
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!currentEngagement || !user) {
        setUserEngagementRole(null);
        return;
      }
      try {
        const team = await apiService.getEngagementTeam(currentEngagement.id);
        const m = team.find((member: any) => String(member.id) === String(user.id));
        setUserEngagementRole(m?.role ?? null);
      } catch {
        setUserEngagementRole(null);
      }
    };
    load();
  }, [currentEngagement, user]);

  useEffect(() => {
    loadCardSignoffs();
  }, [loadCardSignoffs]);

  if (loading) {
    return (
      <div className="p-6 text-white space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-1">Audit Report & Opinion</h2>
          <p className="text-white/60">Loading questions...</p>
        </div>
        <Card className="border-white/10 bg-white/5">
          <CardContent className="p-6">
            <div className="text-center text-white/60">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4da3ff] mx-auto mb-4"></div>
              <p>Loading questions from JSON file...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-white space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-1">Audit Report & Opinion</h2>
          <p className="text-white/60">Error loading questions</p>
        </div>
        <Card className="border-red-500/20 bg-red-500/5">
          <CardContent className="p-6">
            <div className="flex items-center gap-3 text-red-300">
              <AlertCircle className="h-5 w-5" />
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (questions.length === 0) {
    return (
      <div className="p-6 text-white space-y-6">
        <div>
          <h2 className="text-xl font-semibold mb-1">Audit Report & Opinion</h2>
          <p className="text-white/60">No questions found</p>
        </div>
        <Card className="border-white/10 bg-white/5">
          <CardContent className="p-6">
            <div className="text-center text-white/60">
              <FileText className="h-8 w-8 mx-auto mb-4 text-white/40" />
              <p>No questions available. Please check the JSON file.</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-end items-center gap-2 flex-wrap px-6 pt-2">
        <PlanningSignoffTokens signoffs={cardSignoffs} className="mt-0" />
        <Button variant="outline" size="sm" onClick={() => setSignoffDialogOpen(true)} className="border-white/20 text-white hover:bg-white/10">
          <FileSignature className="h-4 w-4 mr-1.5" />
          Sign off
        </Button>
      </div>
      <PlanningSignoffDialog
        isOpen={signoffDialogOpen}
        onClose={() => setSignoffDialogOpen(false)}
        workspaceName="Audit Report & Opinion"
        cardNames={["Audit Report & Opinion"]}
        user={user ? { user_name: (user as any).user_name ?? (user as any).name, type: (user as any).type } : null}
        userEngagementRole={userEngagementRole}
        onSignoffDone={loadCardSignoffs}
      />
      <BaseTailoringQuestions
        areaName="AUDREP"
        title="Audit Report & Opinion"
        description="Answer the following questions based on the audit report and opinion requirements. Refer to the Word document for additional guidance."
        questions={questions}
        onComplete={(answers) => {
          console.log("Audit Report & Opinion answers:", answers);
        }}
      />
    </div>
  );
}
